import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHead from './SEOHead';
import StarryBackground from './StarryBackground';
import { useI18n } from '../i18n';
import { getInviteThumbnail } from '../utils/thumbnailGenerator';

const LAMBDA_URL = 'https://fzybga6gi7iq3jov2uzjah2qra0kbjcm.lambda-url.us-east-1.on.aws/';
const CF_BASE = 'https://d3ii00yw98im3x.cloudfront.net/public/';

const toCF = (path) => {
  if (!path) return undefined;
  const clean = String(path).replace(/^\//, '');
  return CF_BASE + clean;
};

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const ChatInvitationPage = ({ primaryAction, secondaryAction }) => {
  const { roomId } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const intervalRef = useRef(null);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({ action: 'chat_public_info', roomId, ts: new Date().toISOString() }).toString();
        const url = `${LAMBDA_URL}?${qs}`;
        const res = await fetch(url, { method: 'GET' });

        // Gestione sia "envelope" {statusCode, headers, body} che body diretto
        let parsed = null;
        try {
          parsed = await res.json();
        } catch {}

        let body = null;
        if (parsed && typeof parsed === 'object') {
          if (parsed.success !== undefined) {
            body = parsed; // già il body finale
          } else if (typeof parsed.body === 'string') {
            try { body = JSON.parse(parsed.body); } catch { body = null; }
          } else if (parsed.body && typeof parsed.body === 'object') {
            body = parsed.body;
          }
        }

        if (!res.ok || !body || !body.success) {
          const errMsg = (body && body.error) || `HTTP ${res.status}`;
          throw new Error(errMsg);
        }

        if (!cancelled) setData(body);
      } catch (e) {
        console.error('[ChatInvitationPage] fetch error', e);
        if (!cancelled) setError(e.message || 'Errore di rete');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (roomId) fetchData();
    return () => { cancelled = true; };
  }, [roomId]);

  const bgImages = useMemo(() => {
    const imgs = data?.tripPlan?.activityImages || [];
    return shuffle(imgs).map(toCF);
  }, [data]);

  // Slideshow nella card (ogni 3s)
  useEffect(() => {
    if (!bgImages || bgImages.length === 0) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSlideIndex((i) => (i + 1) % bgImages.length);
    }, 3000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [bgImages]);

  const pageTitle = data?.room?.name ? `${t('invite.capsule')} • ${data.room.name} | Wanderbuddy` : `Wanderbuddy`;
  const pageDesc = data?.room?.description || t('seo.default_desc');
  const ogImage = data ? getInviteThumbnail(data.room, data.tripPlan) : undefined;

  const handlePrimaryClick = () => {
    if (isIOS) {
      // Per ora: su iOS apriamo direttamente l'App Store
      window.location.replace('https://apps.apple.com/app/id6737308693');
    } else {
      // Altri device → pagina waitlist
      window.location.assign('/waitlist');
    }
  };

  return (
    <>
      <SEOHead title={pageTitle} description={pageDesc} type="website" image={ogImage} />

      {/* Background starry */}
      <StarryBackground />

      <div className="sora-layout" style={{ position: 'relative', zIndex: 2 }}>
        <motion.main 
          className="sora-main"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <AnimatePresence>
            {loading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="sora-info-box">
                  <strong>Caricamento invito...</strong>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {!loading && error && (
            <div className="sora-info-box" style={{ borderColor: 'rgba(244, 67, 54, 0.3)', background: 'rgba(244, 67, 54, 0.15)' }}>
              <strong>Errore</strong>
              <div style={{ marginTop: 8 }}>{error}</div>
            </div>
          )}

          {!loading && data && (
            <motion.div
              className="sora-hero-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              style={{
                width: '100%',
                maxWidth: 520,
                borderRadius: 24,
                padding: 0,
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
              }}
            >
              {/* Slideshow background nella card */}
              <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                {bgImages.slice(0, 8).map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt="bg"
                    loading="lazy"
                    style={{
                      position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                      opacity: slideIndex % (bgImages.slice(0, 8).length) === idx ? 1 : 0,
                      transition: 'opacity 1s ease',
                      filter: 'brightness(0.75) saturate(110%)'
                    }}
                  />
                ))}
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)' }} />
              </div>

              {/* Contenuto card */}
              <div style={{ position: 'relative', zIndex: 2, padding: '1.25rem' }}>
                {/* Capsule */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.28)',
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    marginBottom: 10,
                    textTransform: 'uppercase'
                  }}
                >
                  <span style={{ fontSize: 10 }}>🎟️</span>
                  <span>{t('invite.capsule')}</span>
                </div>
                <h1 className="sora-hero-title" style={{ margin: 0, fontSize: '2rem' }}>{data.room?.name}</h1>
                {data.room?.description && (
                  <p className="sora-hero-description" style={{ marginTop: 8 }}>{data.room.description}</p>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 14, overflow: 'hidden', background: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
                    {data.creator?.imageURL && (
                      <img src={toCF(data.creator.imageURL)} alt={data.creator?.nickname || 'creator'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                  <div style={{ color: 'white' }}>
                    <div style={{ fontSize: 13, opacity: 0.9 }}>{t('invite.created_by')}</div>
                    <div style={{ fontWeight: 700 }}>{data.creator?.aliasOrName || data.creator?.nickname} <span style={{ opacity: 0.7, fontWeight: 500 }}>@{data.creator?.nickname}</span></div>
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 13, color: 'white', opacity: 0.9, marginBottom: 6 }}>{t('invite.for_trip')}</div>
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8,
                    background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 14,
                    padding: '0.75rem', color: 'white'
                  }}>
                    <div><div style={{ fontSize: 12, opacity: 0.7 }}>{t('invite.destination') || 'Destinazione'}</div><div style={{ fontWeight: 600 }}>{data.tripPlan?.destination || '—'}</div></div>
                    <div><div style={{ fontSize: 12, opacity: 0.7 }}>{t('invite.duration') || 'Durata'}</div><div style={{ fontWeight: 600 }}>{data.tripPlan?.duration || '—'} {t('invite.days') || 'giorni'}</div></div>
                    <div><div style={{ fontSize: 12, opacity: 0.7 }}>{t('invite.type') || 'Tipo'}</div><div style={{ fontWeight: 600 }}>{data.tripPlan?.type || '—'}</div></div>
                    <div><div style={{ fontSize: 12, opacity: 0.7 }}>{t('invite.activities') || 'Attività'}</div><div style={{ fontWeight: 600 }}>{data.tripPlan?.activitiesCount || 0}</div></div>
                    <div><div style={{ fontSize: 12, opacity: 0.7 }}>{t('invite.budget') || 'Budget'}</div><div style={{ fontWeight: 600 }}>{data.tripPlan?.budget || '—'}</div></div>
                  </div>
                </div>

                <div className="sora-actions" style={{ marginTop: 12, gap: '0.75rem' }}>
                  <motion.button
                    type="button"
                    className="sora-btn sora-btn-download"
                    onClick={handlePrimaryClick}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t('landing.cta_store')}
                  </motion.button>
                  <motion.button
                    type="button"
                    className="sora-btn sora-btn-login"
                    onClick={() => setShowHowItWorks(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t('invite.how_it_works')}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </motion.main>

        <motion.footer
          className="sora-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <div className="sora-footer-content">
            <div className="sora-footer-logo">
              <img src="/wanderbuddy-white.png" alt="WanderBuddy" style={{ height: '24px', width: 'auto', opacity: 0.6 }} />
            </div>
            <p className="sora-footer-text">{t('common.footer')}</p>
          </div>
        </motion.footer>
      </div>

      {/* Modal Come funziona */}
      <AnimatePresence>
        {showHowItWorks && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setShowHowItWorks(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '90%', maxWidth: 460,
                background: 'rgba(20,20,20,0.9)', color: 'white',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16,
                padding: '1rem', boxShadow: '0 20px 60px rgba(0,0,0,0.45)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0 }}>{t('invite.how_it_works')}</h3>
                <button onClick={() => setShowHowItWorks(false)} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>
              <ol style={{ marginTop: 12, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>{t('invite.how_step1')}</li>
                <li>{t('invite.how_step2')}</li>
                <li>{t('invite.how_step3')}</li>
              </ol>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatInvitationPage;


